import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { FRAME_IMAGE_FIELDS, frameImageVariantConfig } from '../../util/cardImageVariants';
import { isErrorUserPendingApproval, isForbiddenError, storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import {
  constructQueryParamName,
  isOriginInUse,
  withConfiguredMultiEnumModes,
} from '../../util/search';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { parse } from '../../util/urlHelpers';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

// Pagination page size might need to be dynamic on responsive page layouts
// Initial page size. View More loads 12 at a time (see onLoadMore in SearchPageWithGrid.js).
const RESULT_PAGE_SIZE = 24;

// ================ Helper Functions ================ //

// Story 8.14 (ID-1, ID-7, AC-5) — the default /s order for a search with NO
// keywords: sold horses BELOW every active and on-hold horse across ALL result
// pages, newest first within each. (A keyword search keeps relevance order —
// see sortSearchParams.)
// `publicData.searchRank` is a CLI-set `long` search-schema key written by
// tph-api (2 = active, on hold, or no status; 1 = sold). Sharetribe sorts
// descending by default, so the UNPREFIXED key puts 2 before 1 — do NOT "fix"
// the direction with a minus prefix (the pub_lastActivityAt precedent,
// HomePage.duck.js). A listing with no rank is sorted after every ranked one,
// not excluded (established 2026-09-21); tph-api's writers and nightly sweep
// keep every published listing ranked.
export const SEARCH_RANK_SORT = 'pub_searchRank,createdAt';

// Story 8.14 (ID-2, #2703) — the 90-day `isSoldTooOld` drop is RETIRED, not
// fixed. It read `publicData.soldDate`, which is never public: tph-api writes
// soldDate to PRIVATE data deliberately so buyers cannot read sale timing
// (listingStatusCapture.js), so the filter never fired once. 🔴 Do NOT restore
// it by moving soldDate to publicData — that publishes sale timing to every
// buyer. Sold horses stay in results and sort last server-side (ID-1).
const resultIds = data => {
  const listings = data.data;
  const valid = listings.filter(l => !(l.attributes.deleted || l.attributes.state !== 'published'));
  // Active listings first, sold listings at the bottom within each page
  valid.sort((a, b) => {
    const aIsSold = a.attributes?.publicData?.listingStatus === 'sold' ? 1 : 0;
    const bIsSold = b.attributes?.publicData?.listingStatus === 'sold' ? 1 : 0;
    return aIsSold - bIsSold;
  });
  return valid.map(l => l.id);
};

// ================ Async Thunks ================ //

/////////////////////
// Search Listings //
/////////////////////
const searchListingsPayloadCreator = ({ searchParams, config }, thunkAPI) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = (listingTypes, listingTypePathParam, isListingTypeVariant) => {
    return isListingTypeVariant
      ? {
          pub_listingType: listingTypePathParam,
        }
      : config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const constructCategoryPropertiesForAPI = (queryParamPrefix, categories, level, params) => {
    const levelKey = `${queryParamPrefix}${level}`;
    const levelValue =
      typeof params?.[levelKey] !== 'undefined' ? `${params?.[levelKey]}` : undefined;
    const foundCategory = categories.find(cat => cat.id === levelValue);
    const subcategories = foundCategory?.subcategories || [];
    // Note: we might need to prepare nested categories too: categoryLevel1, categoryLevel2, categoryLevel3
    return foundCategory && subcategories.length > 0
      ? {
          [levelKey]: levelValue,
          ...constructCategoryPropertiesForAPI(queryParamPrefix, subcategories, level + 1, params),
        }
      : foundCategory
      ? { [levelKey]: levelValue }
      : {};
  };

  /**
   * Category filter params are prepared here. We omit invalid category names.
   * I.e. params that are not part of the currently configured category tree.
   *
   * @param {string} paramName - The name of the parameter to prepare.
   * @param {Object} params - The search params object.
   * @returns {Object} The prepared parameter object.
   */
  const prepareCategoryParams = (paramName, params) => {
    const categoryConfig = config.search.defaultFilters?.find(f => f.schemaType === 'category');
    const categories = config.categoryConfiguration.categories;
    const { key, scope } = categoryConfig || {};
    const categoryParamPrefix = constructQueryParamName(key, scope);
    return paramName.startsWith(categoryParamPrefix)
      ? constructCategoryPropertiesForAPI(categoryParamPrefix, categories, 1, params)
      : {};
  };

  const constructIntegerRangePropertyForAPI = (queryParamPrefix, params) => {
    const integerValue = params?.[queryParamPrefix];
    const [min, max] = integerValue ? integerValue.split(',') : [];
    // NOTE: long filter needs exclusive max value on API side
    const inclusiveMin = Number.parseInt(min, 10);
    const exclusiveMax = Number.parseInt(max, 10) + 1;

    // NOTE: currently we don't validate the range values against the integer range config,
    // but we might want to do that in the future.

    return Number.isInteger(inclusiveMin) && Number.isInteger(exclusiveMax)
      ? { [queryParamPrefix]: [inclusiveMin, exclusiveMax].join(',') }
      : {};
  };

  /**
   * Integer range filter values are converted to API params of type 'long'.
   *
   * The range end must be exclusive. E.g. 1000,2000 -> 1000,2001
   *
   * NOTE: currently we don't validate the range values against the integer range config,
   * but we might want to do that in the future.
   *
   * @param {string} paramName - The name of the parameter to prepare.
   * @param {Object} params - The search params object.
   * @returns {Object} The prepared parameter object.
   */
  const prepareIntegerRangeParam = (paramName, params) => {
    const integerRangeConfig = config.listing.listingFields?.find(f => f.schemaType === 'long');
    const { key, scope } = integerRangeConfig || {};
    const integerParamPrefix = constructQueryParamName(key, scope);
    return paramName.startsWith(integerParamPrefix)
      ? constructIntegerRangePropertyForAPI(integerParamPrefix, params)
      : {};
  };

  // This function goes through given params and if there's a specific handler for the parameter type,
  // it calls the handler to prepare the property for API.
  // Otherwise, it just passes the param through.
  const prepareAPIParams = (params, paramHandlers) => {
    const pickedKeys = Object.entries(params).reduce((picked, [k, v]) => {
      const preparedParams = paramHandlers.reduce((picked, fn) => {
        return { ...picked, ...fn(k, params) };
      }, {});

      // If the param is not handled by any of the handlers, we pass it through.
      const currentParam = Object.keys(preparedParams).length > 0 ? preparedParams : { [k]: v };

      return { ...picked, ...currentParam };
    }, {});

    return pickedKeys;
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = config.search.defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          // Availability can be time-full or time-partial.
          // However, due to prolonged time window, we need to use time-partial.
          availability: 'time-partial',
          // minDuration uses minutes
          minDuration,
        }
      : {};
  };

  const stockFilters = datesMaybe => {
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // If dates filter is not in use,
    //   1) Add minStock filter with default value (1)
    //   2) Add relaxed stockMode: "match-undefined"
    // The latter is used to filter out all the listings that explicitly are out of stock,
    // but keeps bookable and inquiry listings.
    return hasDatesFilterInUse ? {} : { minStock: 1, stockMode: 'match-undefined' };
  };

  const seatsSearchParams = (seats, datesMaybe) => {
    const seatsFilter = config.search.defaultFilters.find(f => f.key === 'seats');
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // Seats filter cannot be applied without dates
    return hasDatesFilterInUse && seatsFilter ? { seats } : {};
  };

  const sortSearchParams = (sortParam, hasKeywords) => {
    const sortConfig = config?.search?.sortConfig || {};
    const relevanceEnabled = sortConfig.options?.some(
      option => option.key === sortConfig.relevanceKey
    );

    // User-specified sort takes priority
    if (sortParam !== undefined && sortParam !== sortConfig.relevanceKey) {
      return { sort: sortParam };
    }

    // No sort parameter when keyword search is used or sort config is inactive.
    //
    // 🔴 Story 8.14 — operator ruling 2026-09-21 (supersedes the 2026-09-18
    // Option A ruling). Sharetribe ORs the words of a keyword string, so the
    // matched set is wide and RELEVANCE is what surfaces the right horses:
    // "All horses sired by Aint Seen Nothin Yet" matches 424 listings, and
    // under relevance 15 of the 16 horses by that sire are on page 1 — under an
    // explicit sort, 1 of 16 (measured on production, read-only). So a query
    // carrying `keywords` — the keyword route AND an AI search whose leftover
    // search_terms were sent — keeps relevance order, with the existing
    // within-page sold-to-bottom ordering in resultIds. Only a query with NO
    // keywords sorts on the rank key, so sold sinks across ALL pages there.
    if (relevanceEnabled && (hasKeywords || !sortConfig.active)) {
      return {};
    }

    return { sort: SEARCH_RANK_SORT };
  };

  const {
    perPage,
    price,
    dates,
    seats,
    sort,
    mapSearch,
    listingTypePathParam,
    isListingTypeVariant,
    ...restOfParams
  } = searchParams;
  // The params related to default filters are prepared one-by-one
  // We could consider moving them to the prepareAPIParams function too.
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const stockMaybe = stockFilters(datesMaybe);
  const seatsMaybe = seatsSearchParams(seats, datesMaybe);
  const sortMaybe = sortSearchParams(sort, searchParams?.keywords !== undefined);

  const params = {
    // The params that are related to listing fields and categories are prepared here.
    // We add handler functions that check category and integer range configurations.
    // - With category params, we essentially just omit invalid category names.
    //   I.e. params that are not part of the currently configured category tree.
    // - With integer range params, we prepare the property for API.
    //   I.e. the range end must be exclusive. E.g. 1000,2000 -> 1000,2001
    // Note: invalid independent search params are still passed through
    ...prepareAPIParams(restOfParams, [prepareCategoryParams, prepareIntegerRangeParam]),
    // If the search page variant is of type /s/:listingType, this sets the pub_listingType
    // query parameter to the value of the listing type path parameter. The ordering matters here,
    // since this value overrides any possible pub_listingType value coming from query parameters
    // i.e. the previous row.
    //
    // Only one value is currently supported in pub_listingType – if you want to support e.g.
    // /s/:listingType?pub_listingType=[otherListingType] => pub_listingType=listingType,otherListingType,
    // you'll need to customize a logic that merges the query param and path param values.
    ...searchValidListingTypes(
      config.listing.listingTypes,
      listingTypePathParam,
      isListingTypeVariant
    ),
    ...priceMaybe,
    ...datesMaybe,
    ...stockMaybe,
    ...seatsMaybe,
    ...sortMaybe,
    perPage,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error);
    });
};

export const searchListings = createAsyncThunk(
  'SearchPage/searchListings',
  searchListingsPayloadCreator
);

// Reuses the same payload creator so all filter transforms (price, dates, etc.) run identically.
// Reducer appends results instead of replacing, so accumulated listings grow on each call.
export const loadMoreListings = createAsyncThunk(
  'SearchPage/loadMoreListings',
  searchListingsPayloadCreator
);

//////////////////////////////////////////////////
// Newest listings (empty-state fallback row)   //
//////////////////////////////////////////////////

// Fetched lazily, only when the no-results state actually renders
// (see NoSearchResultsMaybe). Follows the featuredListings.duck.js query
// pattern: newest first, filtered to active listings in the reducer.
const NEWEST_FETCH_COUNT = 32; // 2x over-fetch for 16 shown; sold/on-hold are filtered out below
const NEWEST_SHOWN_COUNT = 16; // 4 columns × 4 rows desktop, single column mobile

const fetchNewestListingsPayloadCreator = ({ config }, thunkAPI) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      // Sharetribe sorts descending by default, so 'createdAt' = newest first
      sort: 'createdAt',
      perPage: NEWEST_FETCH_COUNT,
      page: 1,
      minStock: 1,
      stockMode: 'match-undefined',
      include: ['author', 'images'],
      'fields.listing': [
        'title',
        'price',
        'deleted',
        'state',
        'createdAt',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.cardStyle',
        'publicData.pricingOption',
        'publicData.listingStatus',
        'publicData.holdReason',
        'publicData.birthYear',
        'publicData.color',
        'publicData.sex',
        'publicData.sire',
        'publicData.dam',
        'publicData.damsSire',
        'publicData.disciplines',
        // 🔴 Story 12.15 (AC-1) — WITHOUT THIS KEY THE FEATURE SHIPS INERT AND
        // NOTHING GOES RED. A missing allowlist key yields `undefined`, and
        // `readFocalPoint` treats `undefined` as CENTRE by design, so every
        // card would frame dead centre and look exactly as it does today.
        // Pinned in `cardFramingAllowlists.test.js`.
        'publicData.photoFocalPoints',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        ...FRAME_IMAGE_FIELDS,
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      ...frameImageVariantConfig(),
      'limit.images': 1,
    })
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      dispatch(addMarketplaceEntities(response, { listingFields }));
      return response;
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchNewestListings = createAsyncThunk(
  'SearchPage/fetchNewestListings',
  fetchNewestListingsPayloadCreator
);

// ================ Slice ================ //

const searchPageSlice = createSlice({
  name: 'SearchPage',
  initialState: {
    pagination: null,
    searchParams: null,
    searchInProgress: false,
    searchListingsError: null,
    currentPageResultIds: [],
    // Accumulated IDs across all "View More" pages for the current search/filter state.
    // Reset to [] whenever a new search fires (pending); appended on loadMoreListings.
    accumulatedResultIds: [],
    // Highest page number fetched so far; used to request the next page on "View More".
    viewMorePage: 1,
    activeListingId: null,
    // Story 8.11 (AC1 / AC1a) — the identity of the search whose outcome is in
    // the store, and how that search ended.
    //
    // `completedSearchKey` is the VERBATIM `location.search` string that the
    // in-store `pagination` / `searchListingsError` belong to; `searchOutcome`
    // is 'measured' | 'error' | null. Together they let SearchPageWithGrid prove
    // that the pagination it is about to log was produced BY the search the URL
    // currently describes, which is the gate the 8.11 render race defeated.
    //
    // ⚠️ Both MUST stay plain `string | null` and MUST stay declared here.
    // The whole store is JSON.stringify'd for SSR (server/dataLoader.js:72 →
    // server/renderer.js:120) and the replacer (renderer.js:86-91) transforms
    // only SDK types and error values — it does not whitelist or rename keys, so
    // primitives survive intact while a Date/Map/function would not. And
    // JSON.stringify OMITS keys whose value is `undefined`, so an undeclared
    // field would arrive missing on the client rather than explicitly null, and
    // the server and client state shapes would no longer diff cleanly.
    completedSearchKey: null,
    searchOutcome: null,
    // Empty-state "Newest on The Premier Horse" row (fetched lazily)
    newestListingIds: [],
    newestInProgress: false,
    newestFetched: false,
  },
  reducers: {
    setActiveListing: (state, action) => {
      state.activeListingId = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // ── Initial / filter-change search ───────────────────────────
      .addCase(searchListings.pending, (state, action) => {
        state.searchParams = action.meta.arg.searchParams;
        state.searchInProgress = true;
        state.searchListingsError = null;
        // Reset accumulation so a new search always starts fresh.
        state.accumulatedResultIds = [];
        state.viewMorePage = 1;
        // Story 8.11 (AC1) — a search is in flight, so nothing in the store is a
        // completed outcome. `pagination` deliberately still holds the PREVIOUS
        // search's meta (clearing it would blank the results mid-search), which
        // is precisely why the key must be invalidated here instead.
        state.completedSearchKey = null;
        state.searchOutcome = null;
      })
      .addCase(searchListings.fulfilled, (state, action) => {
        const ids = resultIds(action.payload.data);
        state.currentPageResultIds = ids;
        state.accumulatedResultIds = ids;
        state.pagination = action.payload.data.meta;
        // Track whichever page was loaded (handles deep-link URLs like ?page=3).
        state.viewMorePage = action.meta.arg.searchParams.page || 1;
        state.searchInProgress = false;
        // Story 8.11 (AC1) — stamp WHICH search this pagination belongs to.
        state.completedSearchKey = action.meta.arg.searchKey ?? null;
        state.searchOutcome = 'measured';
      })
      .addCase(searchListings.rejected, (state, action) => {
        console.error(action.payload);
        state.searchInProgress = false;
        state.searchListingsError = action.payload;
        // Story 8.11 (AC2) — the search FAILED. `state.pagination` is untouched
        // here and therefore still holds the previous search's meta; stamping the
        // outcome as 'error' is what stops SearchPageWithGrid reading that stale
        // count and logging it as though this search had succeeded (§1.3).
        state.completedSearchKey = action.meta.arg.searchKey ?? null;
        state.searchOutcome = 'error';
      })
      // ── "View More" append ────────────────────────────────────────
      .addCase(loadMoreListings.pending, state => {
        state.searchInProgress = true;
        state.searchListingsError = null;
      })
      .addCase(loadMoreListings.fulfilled, (state, action) => {
        const newIds = resultIds(action.payload.data);
        state.currentPageResultIds = newIds;
        // Review L2 — dedupe on append, keeping the FIRST occurrence and the
        // existing order. Relevance paging was measured returning the same
        // listing on two pages (Dev Agent Record T1: 290 rows, 289 unique),
        // and the 2026-09-21 ruling keeps relevance on every keyword search,
        // so without this the accumulated grid renders a duplicate card — and
        // a duplicate React key. Keyed on `id.uuid` because these are SDK UUID
        // OBJECTS: two objects for the same listing are never reference-equal,
        // so an identity check would dedupe nothing (`util/data.js:54`).
        const seen = new Set(state.accumulatedResultIds.map(id => id.uuid));
        state.accumulatedResultIds = [
          ...state.accumulatedResultIds,
          ...newIds.filter(id => {
            if (seen.has(id.uuid)) {
              return false;
            }
            seen.add(id.uuid);
            return true;
          }),
        ];
        state.pagination = action.payload.data.meta;
        state.viewMorePage = state.viewMorePage + 1;
        state.searchInProgress = false;
      })
      .addCase(loadMoreListings.rejected, (state, action) => {
        console.error(action.payload);
        state.searchInProgress = false;
        state.searchListingsError = action.payload;
      })
      // ── Newest listings (empty-state row) ────────────────────────
      .addCase(fetchNewestListings.pending, state => {
        state.newestInProgress = true;
      })
      .addCase(fetchNewestListings.fulfilled, (state, action) => {
        // Active listings only: published, not deleted, not sold / on hold
        const listings = action.payload.data.data;
        state.newestListingIds = listings
          .filter(l => {
            const { deleted, state: listingState, publicData } = l.attributes || {};
            const isActiveStatus = !['sold', 'on_hold'].includes(publicData?.listingStatus);
            return !deleted && listingState === 'published' && isActiveStatus;
          })
          .slice(0, NEWEST_SHOWN_COUNT)
          .map(l => l.id);
        state.newestInProgress = false;
        state.newestFetched = true;
      })
      .addCase(fetchNewestListings.rejected, state => {
        // Fail silently — the empty state just omits the newest row.
        state.newestListingIds = [];
        state.newestInProgress = false;
        state.newestFetched = true;
      });
  },
});

// Export the action creator
export const { setActiveListing } = searchPageSlice.actions;

export default searchPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const { listingType: listingTypePathParam } = params || {};
  const state = getState();
  const currentUser = state.user?.currentUser;
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const hasViewingRights = currentUser && hasPermissionToViewData(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData =
    !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized && hasViewingRights);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // `aiKeywords` (Story 8.2 / AC9) is URL state for re-hydrating an AI-parsed
  // search; it is NOT a Sharetribe listing-query param, so strip it here. The
  // structured filter params written alongside it (price, pub_*) drive the
  // actual Sharetribe search.
  const { page = 1, address, origin, aiKeywords, ...urlParams } = queryParams;
  // Story 8.18 (fix review #3) — a multi-enum filter is queried in the mode the
  // page shows (Disciplines: "either"), whatever mode an old link carries.
  const rest = withConfiguredMultiEnumModes(urlParams, config?.listing?.listingFields);
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  const listingTypeVariantMaybe = listingTypePathParam
    ? { listingTypePathParam, isListingTypeVariant: true }
    : {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const searchListingsCall = searchListings({
    // Story 8.11 (AC1) — the VERBATIM router query string this search was
    // dispatched for, carried through the thunk so the fulfilled/rejected
    // reducers can stamp it on the outcome.
    //
    // ⚠️ It must be `search` itself and NOT a reconstruction from `searchParams`:
    // `aiKeywords` is stripped at the destructure above (it is not a Sharetribe
    // listing-query param), so `state.searchParams` structurally CANNOT identify
    // an ai_iso search — the exact population carrying the fabricated rows.
    // Normalised to '' only so a non-string caller still compares by value
    // against `location.search`, which react-router always supplies as a string.
    searchKey: typeof search === 'string' ? search : '',
    searchParams: {
      ...rest,
      ...originMaybe,
      ...listingTypeVariantMaybe,
      page,
      perPage: RESULT_PAGE_SIZE,
      include: ['author', 'images'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'deleted',
        'state',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.cardStyle',
        // These help rendering of 'purchase' listings,
        // when transitioning from search page to listing page
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
        'publicData.priceVariationsEnabled',
        'publicData.priceVariants',
        'publicData.pricingOption',
        'publicData.listingStatus',
        'publicData.holdReason',
        'publicData.birthYear',
        'publicData.color',
        'publicData.sex',
        'publicData.sire',
        'publicData.dam',
        'publicData.damsSire',
        'publicData.disciplines',
        'description',
        // 🔴 Story 12.15 (AC-1) — see the note on the newest query above. This
        // is the main results grid: `SearchResultsPanel`, `NoSearchResultsMaybe`
        // and `SearchMapInfoCard` all feed from here.
        'publicData.photoFocalPoints',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        ...FRAME_IMAGE_FIELDS,
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      ...frameImageVariantConfig(),
      'limit.images': 1,
    },
    config,
  });

  return dispatch(searchListingsCall);
};
